import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { combineLatest, EMPTY, of, Subscription } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { JumbotronVariantEnum } from '~/attendee/components/shared/jumbotron/enums/jumbotron-variant.enum';
import { ComponentTypeEnum } from '~/attendee/enums/component.enum';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { DynamicPageCms } from '~/cms/interfaces/dynamic-page.cms.interface';
import { CmsService } from '~/cms/services/cms/cms.service';
import { CustomHeadTagService } from '~/core/services/custom-head-tag/custom-head-tag.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { OpenGraphPropertyEnum } from '~/core/services/open-graph/enums/open-graph-property.enum';
import { OpenGraphService } from '~/core/services/open-graph/open-graph.service';
import { TitleService } from '~/core/services/title/title.service';

const DYNAMIC_LOADER_KEY = 'loadDynamics';

@Component({
  selector: 'app-attendee-dynamic-page',
  templateUrl: './dynamic-page.component.html',
  styleUrls: ['./dynamic-page.component.scss'],
})
export class DynamicPageComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly componentTypes = ComponentTypeEnum;
  public cmsPage: DynamicPageCms;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cms: CmsService,
    private readonly titleService: TitleService,
    private readonly loaderService: LoaderService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly openGraphService: OpenGraphService,
    private readonly customHeadTag: CustomHeadTagService,
    private readonly router: Router
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.openGraphService.removeMeta();
    this.customHeadTag.destroyMicrodata();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap])
        .pipe(
          tap(() => this.loaderService.start(DYNAMIC_LOADER_KEY)),
          switchMap(([params, queryParams]) =>
            this.cms.getDynamicPageBySlug(params.get('slug')).pipe(
              switchMap((cmsPage) => {
                if (!cmsPage) {
                  const translatedPath =
                    this.localizeRouterService.translateRoute([
                      '/attendee.not-found',
                    ]) as string[];
                  this.router.navigate(translatedPath);
                  return EMPTY;
                }
                return of({ queryParams, cmsPage });
              })
            )
          ),
          map(({ cmsPage }) => {
            this.cmsPage = cmsPage;
            this.titleService.setPageTitle(cmsPage.page_title);
            this.setBreadcrumb();
            this.customHeadTag.addSeoTags(cmsPage?.SEO);
            if (cmsPage.jumbotron?.default) {
              this.openGraphService.setMeta([
                {
                  property: OpenGraphPropertyEnum.IMAGE,
                  content: cmsPage.jumbotron?.default.imageDesktop?.url,
                },
              ]);
            }
          }),
          tap(() => this.loaderService.stop(DYNAMIC_LOADER_KEY))
        )
        .subscribe()
    );
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute('attendee.homepage')
        .toString(),
    };
  }

  public get jumbotronVariant(): JumbotronVariantEnum {
    if (this.cmsPage?.jumbotron?.default?.isFullWidth) {
      return JumbotronVariantEnum.FullWidthImage;
    }
    return JumbotronVariantEnum.Default;
  }

  private setBreadcrumb(): void {
    this.customHeadTag.setBreadcrumb([
      this.customHeadTag.newBreadcrumItem(this.cmsPage.page_title),
    ]);
  }
}
